// Security utilities for input validation and sanitization

export const sanitizeInput = (input: string): string => {
  // Basic XSS prevention - remove script tags and event handlers
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .trim();
};

export const validateContentLength = (content: string, maxLength: number = 2000): { isValid: boolean; error?: string } => {
  if (!content || content.trim().length === 0) {
    return { isValid: false, error: 'Content cannot be empty' };
  }
  
  if (content.length > maxLength) {
    return { isValid: false, error: `Content exceeds maximum length of ${maxLength} characters` };
  }
  
  return { isValid: true };
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateUsername = (username: string): { isValid: boolean; error?: string } => {
  if (!username) {
    return { isValid: false, error: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters long' };
  }
  
  if (username.length > 20) {
    return { isValid: false, error: 'Username must be less than 20 characters long' };
  }
  
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    return { isValid: false, error: 'Username can only contain letters, numbers, underscores, and hyphens' };
  }
  
  return { isValid: true };
};

// Rate limiting helper for client-side
export class ClientRateLimit {
  private attempts: Map<string, number[]> = new Map();
  
  constructor(
    private maxAttempts: number = 5,
    private windowMs: number = 60000 // 1 minute
  ) {}
  
  isAllowed(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const validAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (validAttempts.length >= this.maxAttempts) {
      return false;
    }
    
    validAttempts.push(now);
    this.attempts.set(key, validAttempts);
    return true;
  }
  
  getRemainingTime(key: string): number {
    const attempts = this.attempts.get(key) || [];
    if (attempts.length < this.maxAttempts) return 0;
    
    const oldestAttempt = Math.min(...attempts);
    const resetTime = oldestAttempt + this.windowMs;
    return Math.max(0, resetTime - Date.now());
  }
}

// Content policy checker
export const checkContentPolicy = (content: string): { isValid: boolean; warnings: string[] } => {
  const warnings: string[] = [];
  const lowerContent = content.toLowerCase();
  
  // Check for excessive caps
  const capsRatio = (content.match(/[A-Z]/g) || []).length / content.length;
  if (capsRatio > 0.7 && content.length > 10) {
    warnings.push('Consider reducing the use of capital letters');
  }
  
  // Check for repeated characters
  if (/(.)\1{4,}/.test(content)) {
    warnings.push('Avoid excessive repeated characters');
  }
  
  // Check for suspicious patterns (basic spam detection)
  const spamPatterns = [
    /\b(buy now|click here|limited time|act fast)\b/i,
    /\b(free money|easy money|get rich quick)\b/i,
    /(\.com|\.net|\.org)\b/i
  ];
  
  spamPatterns.forEach(pattern => {
    if (pattern.test(content)) {
      warnings.push('Content may appear promotional or spam-like');
    }
  });
  
  return {
    isValid: warnings.length === 0,
    warnings
  };
};

// Convenience wrapper: sanitize + validate in one call
export const sanitizeAndValidate = (
  input: string,
  maxLength: number = 2000
): { sanitized: string; error?: string } => {
  const sanitized = sanitizeInput(input).trim();
  const validation = validateContentLength(sanitized, maxLength);
  if (!validation.isValid) {
    return { sanitized, error: validation.error };
  }
  return { sanitized };
};

export const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// SQL LIKE pattern sanitization - escapes special characters that have meaning in SQL LIKE queries
// This prevents SQL injection via LIKE pattern manipulation
export const sanitizeForLikeQuery = (input: string): string => {
  if (!input) return '';
  // Escape SQL LIKE special characters: % (wildcard), _ (single char), \ (escape char)
  return input
    .replace(/\\/g, '\\\\')  // Escape backslashes first
    .replace(/%/g, '\\%')    // Escape percent signs
    .replace(/_/g, '\\_');   // Escape underscores
};

// Validate and sanitize search query with length limit
export const sanitizeSearchQuery = (input: string, maxLength: number = 100): string => {
  if (!input) return '';
  // Trim and limit length
  const trimmed = input.trim().slice(0, maxLength);
  // Apply LIKE sanitization
  return sanitizeForLikeQuery(trimmed);
};